import { useCallback, useEffect, useRef, useState } from "react";
import { AUTO_PLAY_COUNTDOWN_MS, goesOn, type AutoPlayEvent } from "./autoPlay";

/**
 * useAutoPlayCountdown is a countdown under way at the end of a chapter
 * (ADR-0177): `start` begins it, `event` applies what happened to it — the
 * rule in autoPlay.ts says whether that goes on or cancels — and `onGo` opens
 * the next chapter. A hidden tab is an event like any other; leaving the page
 * unmounts it, which cancels.
 */
export function useAutoPlayCountdown(onGo: () => void) {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const running = useRef(false);
  const go = useRef(onGo);
  useEffect(() => {
    go.current = onGo;
  }, [onGo]);

  const start = useCallback(() => {
    if (running.current) return;
    running.current = true;
    const at = Date.now();
    setNow(at);
    setStartedAt(at);
  }, []);

  const event = useCallback((e: AutoPlayEvent) => {
    if (!running.current) return;
    running.current = false;
    setStartedAt(null);
    if (goesOn(e)) go.current();
  }, []);

  useEffect(() => {
    if (startedAt == null) return;
    const done = setTimeout(() => event("elapsed"), AUTO_PLAY_COUNTDOWN_MS);
    // Once a second, for the button's spoken count; the fill is a CSS animation.
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const onVisibility = () => {
      if (document.hidden) event("hidden");
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(done);
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [startedAt, event]);

  useEffect(
    () => () => {
      running.current = false;
    },
    [],
  );

  const secondsLeft =
    startedAt == null
      ? 0
      : Math.max(0, Math.ceil((AUTO_PLAY_COUNTDOWN_MS - (now - startedAt)) / 1000));
  return { running: startedAt != null, secondsLeft, start, event };
}
